import { auth } from '../firebase';
import { formatMoney } from './currencyUtil';
import { formatPlanDate, formatPlanDuration } from './subscriptionDateUtil';
import { logPosAdminEvent } from './posSubscriptionUtil';
import { KAWNTER_BE_EMAIL_ROUTES } from '../constants/bookingConst';
import { POS_ADMIN_AUDIT_ACTIONS, POS_SIGNUP_TYPES } from '../constants/posConst';

// Sends Kawnter's emails through kawnter-be (the Node server on Vercel that
// talks to Brevo) — the court-booking system's emailServiceUtil, for the POS.
//
// Every request carries the signed-in account's Firebase ID token, which
// kawnter-be verifies. Every browser is signed in before anything renders
// (AuthGuard), so the landing page has a token too; the admin-only emails
// are refused by the server unless the token is an administrator's.
//
// Nothing here throws. An email is a courtesy on top of a save that has
// already happened — the request or the approval is in Firebase either way —
// so a failed send is reported in the result, never raised.

/**
 * kawnter-be's base URL, without a trailing slash. '' when the build has no
 * REACT_APP_KAWNTER_BE_URL, which switches every email off.
 *
 * @returns {string}
 */
const kawnterBeUrl = () => String(process.env.REACT_APP_KAWNTER_BE_URL || '').trim().replace(/\/+$/, '');

/**
 * Whether this build is set up to send emails at all.
 *
 * @returns {boolean}
 */
export const isEmailServiceConfigured = () => Boolean(kawnterBeUrl());

/**
 * The result every sender returns when this build has no kawnter-be URL.
 *
 * @returns {{success: boolean, skipped: boolean, message: string, data: null}}
 */
const notConfiguredResult = () => ({
  success: false, skipped: true, message: 'Email sending is not set up for this build.', data: null,
});

/**
 * POSTs one JSON payload to kawnter-be as the signed-in account.
 *
 * @param {string} route - a KAWNTER_BE_EMAIL_ROUTES value
 * @param {object} payload
 * @param {object} [options]
 * @param {boolean} [options.keepalive=false] - let the browser finish the
 *   request after the page closes. Used by the landing checkout tab, which
 *   closes itself CHECKOUT_CLOSE_DELAY_MS after submitting — without it the
 *   request is cancelled mid-flight and nothing is sent.
 * @returns {Promise<{success: boolean, skipped: boolean, message: string, data: object|null}>}
 *   `skipped` is true when the build has no kawnter-be URL configured.
 */
export const postToKawnterBe = async (route, payload, { keepalive = false } = {}) => {
  const baseUrl = kawnterBeUrl();
  if (!baseUrl) return notConfiguredResult();

  try {
    // getIdToken() returns the cached token while it has more than five
    // minutes left, so this is normally instant — which matters for the
    // keepalive caller, whose tab is about to close.
    const token = auth?.currentUser ? await auth.currentUser.getIdToken() : '';
    if (!token) {
      return { success: false, skipped: false, message: 'No signed-in account to send the email as.', data: null };
    }

    const response = await fetch(`${baseUrl}${route}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      keepalive,
    });

    // A proxy error page is not JSON; read it as "no details" rather than
    // letting the parse failure hide the status code.
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success !== true) {
      const message = body.message || body.error?.message || `The email server answered ${response.status}.`;
      console.error(`Email request to ${route} failed:`, message);
      return { success: false, skipped: false, message, data: body.data || null };
    }
    return { success: true, skipped: false, message: body.message || '', data: body.data || null };
  } catch (error) {
    console.error(`Email request to ${route} failed:`, error);
    return { success: false, skipped: false, message: 'The email server could not be reached.', data: null };
  }
};

/**
 * Records an admin-triggered email on the administrators' trail, sent or not.
 * Skipped emails (no URL configured) are not recorded — nothing was attempted.
 *
 * @param {object} result - from postToKawnterBe
 * @param {object} details
 * @param {string} details.kind - which email, e.g. 'signup_completed'
 * @param {string} details.entityId - the request or application id
 * @param {string} details.to - recipient address
 * @param {object} [details.extra] - anything else worth keeping in the summary
 * @returns {void}
 */
const auditEmail = (result, { kind, entityId, to, extra = {} }) => {
  if (result.skipped) return;
  logPosAdminEvent({
    action: result.success ? POS_ADMIN_AUDIT_ACTIONS.CLIENT_EMAIL_SENT : POS_ADMIN_AUDIT_ACTIONS.CLIENT_EMAIL_FAILED,
    entityId,
    summary: {
      kind,
      to,
      ...extra,
      ...(result.success ? {} : { error: result.message }),
    },
  });
};

/**
 * A plan's length as the emails print it ("1 month"), or '' for a custom
 * plan, which has no fixed length.
 *
 * @param {{isCustom?: boolean, durationValue?: number, durationUnit?: string}} record
 * @returns {string}
 */
const durationLabel = (record) => (record?.isCustom || !record?.durationValue
  ? ''
  : formatPlanDuration(record.durationValue, record.durationUnit));

/**
 * Case 1 — emails the visitor ("we received your request") and the Kawnter
 * admins ("new request") once a landing-page sign-up is saved: a paid
 * subscription, a free trial or a demo call.
 *
 * Fire-and-forget: call it after the save resolves and do not wait on it.
 *
 * @param {object} request - the saved request, as returned by
 *   submitSubscribeRequest / submitTrialRequest / submitDemoRequest
 * @param {object} [config] - for the currency symbol on the total
 * @param {object} [options]
 * @param {boolean} [options.keepalive] - see postToKawnterBe
 * @returns {Promise<object>} the postToKawnterBe result
 */
export const sendSignupReceivedEmail = (request, config = null, { keepalive = false } = {}) => postToKawnterBe(
  KAWNTER_BE_EMAIL_ROUTES.SIGNUP_RECEIVED,
  {
    requestId: request.id,
    type: request.type,
    name: request.name,
    business: request.business,
    email: request.email,
    phone: request.phone,
    planName: request.planName || '',
    planDuration: durationLabel(request),
    // Free trials and demo calls carry a total of 0; showing "₱0 paid" on
    // them reads like a billing error.
    totalLabel: request.type === POS_SIGNUP_TYPES.SUBSCRIBE ? formatMoney(request.total, config) : '',
    payApp: request.payApp || '',
    staffEmails: request.staffEmails || [],
    warehouse: request.warehouse || 0,
    callDateLabel: request.callDate ? formatPlanDate(request.callDate) : '',
    slotLabel: request.slotLabel || '',
    about: request.about || '',
    features: request.features || '',
  },
  { keepalive },
);

/**
 * Case 2.1 — emails the visitor once an administrator marks their sign-up
 * request Done: "your till is ready" for a subscription or a trial, a
 * thank-you for a demo call. Recorded on the admin trail.
 *
 * @param {object} request - from subscribeSignupRequests
 * @returns {Promise<object>} the postToKawnterBe result
 */
export const sendSignupCompletedEmail = async (request) => {
  const result = await postToKawnterBe(KAWNTER_BE_EMAIL_ROUTES.SIGNUP_COMPLETED, {
    requestId: request.id,
    type: request.type,
    name: request.name,
    business: request.business,
    email: request.email,
    planName: request.planName || '',
    planDuration: durationLabel(request),
  });
  auditEmail(result, {
    kind: 'signup_completed',
    entityId: request.id,
    to: request.email,
    extra: { type: request.type, business: request.business },
  });
  return result;
};

/**
 * Case 2.2 — emails a client once an administrator approves and activates
 * their pending subscription application. Recorded on the admin trail.
 *
 * Sent to the address on the client's profile (Client Registration ->
 * email). A client registered without one is skipped and reported, rather
 * than mailed at a staff login that may not be a real inbox.
 *
 * @param {object} details
 * @param {object} details.application - the approved application
 * @param {object} details.subscription - what approveSubscriptionApplication activated
 * @param {string[]} details.addedStaff
 * @param {boolean} details.reEnabled
 * @param {string} details.clientEmail - from the client's profile; '' when none
 * @param {object} [config] - for the currency symbol on the total
 * @returns {Promise<object>} the postToKawnterBe result; `noRecipient` is true
 *   when the client has no email on file
 */
export const sendApplicationApprovedEmail = async ({
  application, subscription, addedStaff, reEnabled, clientEmail,
}, config = null) => {
  // Checked first so a build without email set up says nothing at all,
  // rather than warning about a missing address it would not have used.
  if (!isEmailServiceConfigured()) return notConfiguredResult();
  if (!clientEmail) {
    return {
      success: false, skipped: true, noRecipient: true, message: 'This client has no email on its profile.', data: null,
    };
  }

  const result = await postToKawnterBe(KAWNTER_BE_EMAIL_ROUTES.APPLICATION_APPROVED, {
    applicationId: application.id,
    clientId: application.clientId,
    clientName: application.clientName,
    email: clientEmail,
    planName: subscription.planName,
    planDuration: durationLabel(application),
    startDateLabel: formatPlanDate(subscription.startDate),
    endDateLabel: formatPlanDate(subscription.endDate),
    totalLabel: formatMoney(application.total, config),
    addedStaff: addedStaff || [],
    reEnabled: reEnabled === true,
  });
  auditEmail(result, {
    kind: 'application_approved',
    entityId: application.id,
    to: clientEmail,
    extra: { client: application.clientId, plan: subscription.planName },
  });
  return result;
};

/**
 * One line for the admin's result dialog saying what happened to an email.
 * '' when email sending is not set up, so the dialog stays as it was.
 *
 * @param {object} result - from one of the send functions above
 * @param {string} to - who it was for
 * @returns {string}
 */
export const describeEmailResult = (result, to) => {
  if (result.noRecipient) return 'No email was sent: this client has no email on its profile.';
  if (result.skipped) return '';
  if (result.success) return `A confirmation email was sent to ${to}.`;
  return `The confirmation email to ${to} could not be sent: ${result.message}`;
};
